import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CalendarCheck, Lock, Printer, TrendingUp, ClipboardList, Coins, Ban } from 'lucide-react';
import { useToast, useConfirm, PageHeader, Button, Input, SkeletonCard } from '../components/ui';
import { fmtMoney } from '../utils/currency';

const fmt = (n) => fmtMoney(n);
const todayStr = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local

const authHeaders = () => ({ headers: { 'x-auth-token': localStorage.getItem('token') } });

const DiffBadge = ({ value }) => {
  if (Math.abs(value) < 0.005) {
    return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-success-500/15 text-success-700">BALANCED</span>;
  }
  return value < 0
    ? <span className="text-xs font-semibold px-2 py-0.5 rounded bg-danger-500/15 text-danger-700">SHORT {fmt(Math.abs(value))}</span>
    : <span className="text-xs font-semibold px-2 py-0.5 rounded bg-warning-500/15 text-warning-700">OVER {fmt(value)}</span>;
};

// Tailwind needs literal class names, so tones are spelled out
const TONES = {
  primary: 'bg-primary-50 text-primary-600',
  danger: 'bg-danger-50 text-danger-600',
};

const StatCard = ({ label, value, sub, icon: Icon, tone = 'primary' }) => (
  <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 flex items-center justify-between">
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="text-2xl font-bold text-neutral-900 tabular-nums mt-1">{value}</p>
      {sub && <p className="text-xs text-neutral-500 mt-0.5">{sub}</p>}
    </div>
    <div className={`w-10 h-10 rounded-lg ${TONES[tone] || TONES.primary} flex items-center justify-center flex-shrink-0`}>
      <Icon size={20} />
    </div>
  </div>
);

const Closing = () => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null); // { closed, closing, summary }
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countedCash, setCountedCash] = useState('');
  const [notes, setNotes] = useState('');
  const [closing, setClosing] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/closings/summary', {
        ...authHeaders(),
        params: { date, tzOffset: new Date().getTimezoneOffset() },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error loading day summary');
    } finally {
      setLoading(false);
    }
  }, [date, toast]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get('/api/closings', authHeaders());
      setHistory(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleClose = async () => {
    const counted = parseFloat(countedCash);
    if (Number.isNaN(counted) || counted < 0) {
      toast.error('Enter the counted drawer amount first');
      return;
    }
    const diff = counted - (data?.summary?.expectedCash || 0);
    const ok = await confirm({
      title: `Close ${date}?`,
      message: `Counted cash: ${fmt(counted)} · Expected: ${fmt(data?.summary?.expectedCash)} · ` +
        (Math.abs(diff) < 0.005 ? 'Drawer is balanced.' : diff < 0 ? `Drawer is SHORT ${fmt(Math.abs(diff))}.` : `Drawer is OVER ${fmt(diff)}.`) +
        ' This locks the day permanently.',
      confirmText: 'Close day',
    });
    if (!ok) return;

    setClosing(true);
    try {
      await axios.post('/api/closings', {
        date,
        tzOffset: new Date().getTimezoneOffset(),
        countedCash: counted,
        notes,
      }, authHeaders());
      toast.success(`Day ${date} closed`);
      setCountedCash('');
      setNotes('');
      fetchSummary();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error closing day');
    } finally {
      setClosing(false);
    }
  };

  // When the day is closed we show the frozen record; otherwise live numbers.
  const s = data?.closed ? data.closing : data?.summary;
  const isClosed = !!data?.closed;
  const liveDiff = countedCash !== '' ? parseFloat(countedCash) - (s?.expectedCash || 0) : null;

  return (
    <>
      <PageHeader
        title="Day Close"
        subtitle="End-of-day sales summary and cash drawer reconciliation (Z-report)."
        action={
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              max={todayStr()}
              onChange={(e) => setDate(e.target.value)}
              className="border border-neutral-300 rounded-md px-3 py-1.5 text-sm bg-white"
            />
            <Button variant="secondary" size="sm" onClick={() => window.print()}>
              <Printer size={15} className="mr-1.5" /> Print
            </Button>
          </div>
        }
      />

      {loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      )}

      {!loading && s && (
        <>
          {isClosed && (
            <div className="mb-4 flex items-center gap-2 bg-neutral-900 text-white rounded-lg px-4 py-2.5 text-sm">
              <Lock size={15} />
              <span>
                Day closed by <strong>{data.closing.closedByName || 'manager'}</strong>
                {' '}on {new Date(data.closing.createdAt).toLocaleString()} — figures below are the frozen record.
              </span>
            </div>
          )}

          {/* Summary stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Gross sales" value={fmt(s.grossSales)} sub={`${s.ordersCompleted} paid orders`} icon={TrendingUp} />
            <StatCard label="Tips" value={fmt(s.tips)} icon={Coins} />
            <StatCard label="Orders" value={s.ordersCompleted} sub="completed & paid" icon={ClipboardList} />
            <StatCard label="Cancelled" value={s.ordersCancelled} icon={Ban} tone="danger" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Breakdowns */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
                <div className="px-5 py-3 border-b border-neutral-200">
                  <h2 className="text-sm font-semibold text-neutral-800">By payment method</h2>
                </div>
                <table className="app-table">
                  <thead><tr><th>Method</th><th>Orders</th><th className="text-right">Amount (incl. tip)</th></tr></thead>
                  <tbody>
                    {(s.byMethod || []).length === 0 && (
                      <tr><td colSpan="3" className="text-neutral-400 text-sm py-4 text-center">No paid orders this day</td></tr>
                    )}
                    {(s.byMethod || []).map(m => (
                      <tr key={m.method}>
                        <td className="font-medium capitalize">{String(m.method).replace('_', ' ')}</td>
                        <td className="tabular-nums">{m.count}</td>
                        <td className="tabular-nums text-right font-medium">{fmt(m.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
                <div className="px-5 py-3 border-b border-neutral-200">
                  <h2 className="text-sm font-semibold text-neutral-800">By order type</h2>
                </div>
                <table className="app-table">
                  <thead><tr><th>Type</th><th>Orders</th><th className="text-right">Sales</th></tr></thead>
                  <tbody>
                    {(s.byType || []).map(m => (
                      <tr key={m.orderType}>
                        <td className="font-medium capitalize">{m.orderType}</td>
                        <td className="tabular-nums">{m.count}</td>
                        <td className="tabular-nums text-right font-medium">{fmt(m.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
                <div className="px-5 py-3 border-b border-neutral-200">
                  <h2 className="text-sm font-semibold text-neutral-800">Per cashier / waiter</h2>
                </div>
                <table className="app-table">
                  <thead><tr><th>Name</th><th>Orders</th><th className="text-right">Sales</th></tr></thead>
                  <tbody>
                    {(s.byCashier || []).map((c, i) => (
                      <tr key={c.user || i}>
                        <td className="font-medium">{c.name}</td>
                        <td className="tabular-nums">{c.count}</td>
                        <td className="tabular-nums text-right font-medium">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Cash drawer */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden h-fit">
              <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
                <CalendarCheck size={16} className="text-primary-600" />
                <h2 className="text-sm font-semibold text-neutral-800">Cash drawer</h2>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Expected cash (sales + tips)</span>
                  <span className="font-bold tabular-nums">{fmt(s.expectedCash)}</span>
                </div>
                {s.cashExpenses > 0 && (
                  <div className="flex justify-between text-xs text-neutral-500">
                    <span>Expenses recorded this day (info)</span>
                    <span className="tabular-nums">{fmt(s.cashExpenses)}</span>
                  </div>
                )}

                {isClosed ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Counted by manager</span>
                      <span className="font-bold tabular-nums">{fmt(data.closing.countedCash)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm border-t border-neutral-200 pt-3">
                      <span className="text-neutral-600">Difference</span>
                      <DiffBadge value={data.closing.difference} />
                    </div>
                    {data.closing.notes && (
                      <p className="text-xs text-neutral-500 bg-neutral-50 rounded p-2 whitespace-pre-wrap">{data.closing.notes}</p>
                    )}
                  </>
                ) : (
                  <>
                    <Input
                      label="Counted cash in drawer"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Count the drawer and enter the amount"
                      value={countedCash}
                      onChange={(e) => setCountedCash(e.target.value)}
                    />
                    {liveDiff !== null && !Number.isNaN(liveDiff) && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-neutral-600">Difference</span>
                        <DiffBadge value={liveDiff} />
                      </div>
                    )}
                    <Input
                      label="Notes (optional)"
                      type="text"
                      placeholder="e.g. reason for short/over"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                    <div className="pt-1">
                      <Button variant="primary" fullWidth loading={closing} onClick={handleClose}>
                        <Lock size={15} className="mr-1.5" /> Close day
                      </Button>
                      <p className="text-[11px] text-neutral-400 mt-2 text-center">
                        Closing freezes this day's figures permanently.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* History */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-200">
          <h2 className="text-sm font-semibold text-neutral-800">Past closings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                <th>Date</th><th>Orders</th><th className="text-right">Gross</th>
                <th className="text-right">Expected cash</th><th className="text-right">Counted</th>
                <th>Difference</th><th>Closed by</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan="7" className="text-neutral-400 text-sm py-4 text-center">No closings yet — close your first day above.</td></tr>
              )}
              {history.map(h => (
                <tr key={h._id} className="cursor-pointer hover:bg-neutral-50" onClick={() => setDate(h.date)}>
                  <td className="font-medium">{h.date}</td>
                  <td className="tabular-nums">{h.ordersCompleted}</td>
                  <td className="tabular-nums text-right">{fmt(h.grossSales)}</td>
                  <td className="tabular-nums text-right">{fmt(h.expectedCash)}</td>
                  <td className="tabular-nums text-right">{fmt(h.countedCash)}</td>
                  <td><DiffBadge value={h.difference} /></td>
                  <td className="text-neutral-600">{h.closedByName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Closing;
